import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { createProcedureThunk, updateProcedureThunk } from "../../redux/slices/procedureSlice";
import { getApiErrorMessage } from "../../utils/apiError";
import { REPORT_CATEGORIES } from "../../constants/reportCategories";

const EMPTY = {
  procedureName: "", procedureCode: "", reportCategory: "", description: "", defaultDuration: "",
  chargeNormal: "", chargeArab: "", taxApplicable: false, status: "Active",
};

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const categoryOptions = [{ value: "", label: "None" }, ...REPORT_CATEGORIES.map(({ value, label }) => ({ value, label }))];

const ProcedureModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editRecord ? { ...EMPTY, ...editRecord } : EMPTY);
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setSelect = (field) => (opt) => setForm((f) => ({ ...f, [field]: opt?.value ?? "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.procedureName.trim()) {
      Swal.fire("Required", "Procedure Name is required.", "warning"); return;
    }
    setSaving(true);
    try {
      // The numeric inputs start as "" and stay "" when left blank. The API binds them to
      // int / decimal, which cannot accept an empty string, so posting the form as-is failed
      // model binding with a 400 before the controller ran. Coerce to the model's own
      // defaults instead (duration 30, charges 0).
      const payload = {
        ...form,
        defaultDuration: Number(form.defaultDuration) || 30,
        chargeNormal: Number(form.chargeNormal) || 0,
        chargeArab: Number(form.chargeArab) || 0,
        procedureCode: (form.procedureCode || "").trim(),
        reportCategory: form.reportCategory || null,
      };
      if (editRecord?.id) {
        await dispatch(updateProcedureThunk({ id: editRecord.id, data: payload })).unwrap();
      } else {
        await dispatch(createProcedureThunk(payload)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Procedure ${editRecord ? "updated" : "added"} successfully`, showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save procedure."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Procedure" : "Add Procedure"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-8 mb-3">
                    <label className="form-label">Procedure Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.procedureName} onChange={set("procedureName")} placeholder="e.g. Root Canal Treatment" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Code</label>
                    <input className="form-control" value={form.procedureCode || ""} onChange={set("procedureCode")} placeholder="e.g. D178" />
                  </div>
                  <div className="col-lg-8 mb-3">
                    <label className="form-label">Report Category</label>
                    <Select classNamePrefix="react-select" options={categoryOptions}
                      value={categoryOptions.find((o) => o.value === (form.reportCategory || "")) || null}
                      onChange={setSelect("reportCategory")}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Duration (minutes)</label>
                    <input type="number" className="form-control" value={form.defaultDuration} onChange={set("defaultDuration")} placeholder="30" min="10" />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" rows={2} value={form.description} onChange={set("description")} placeholder="Brief description" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Charge — Normal (₹)</label>
                    <input type="number" className="form-control" value={form.chargeNormal} onChange={set("chargeNormal")} placeholder="0.00" min="0" step="0.01" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Charge — Arab (₹)</label>
                    <input type="number" className="form-control" value={form.chargeArab} onChange={set("chargeArab")} placeholder="0.00" min="0" step="0.01" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Status</label>
                    <Select classNamePrefix="react-select" options={statusOptions}
                      value={statusOptions.find((o) => o.value === form.status) || null}
                      onChange={setSelect("status")}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-4 mb-3 d-flex align-items-end">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="taxApplicable"
                        checked={!!form.taxApplicable}
                        onChange={(e) => setForm((f) => ({ ...f, taxApplicable: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="taxApplicable">Tax Applicable</label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add Procedure"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default ProcedureModal;
